import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";

// In-memory cache so we never hit the API twice for the same word.
const dictCache = new Map();

/**
 * Wraps a single English word with a hoverable popover that fetches a
 * Chinese meaning via /api/dictionary on demand.
 */
export function HoverWord({ word }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(() => dictCache.get(word.toLowerCase()) || null);
  const [loading, setLoading] = useState(false);

  const fetchIfNeeded = async (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) return;
    const key = word.toLowerCase();
    if (dictCache.has(key)) {
      setData(dictCache.get(key));
      return;
    }
    setLoading(true);
    try {
      const { data: d } = await api.post("/dictionary", { word });
      dictCache.set(key, d);
      setData(d);
    } catch {
      setData({ cn: "查询失败", pos: "", example_en: "", example_cn: "" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={fetchIfNeeded}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline underline decoration-dotted decoration-slate-300 underline-offset-4 hover:decoration-sky-500 hover:text-sky-700 cursor-help transition-colors"
          data-testid={`hover-word-${word.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
        >
          {word}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="w-64 rounded-2xl border-slate-200 shadow-lg"
      >
        <div className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">
          {word}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Looking up… 查询中…</span>
          </div>
        ) : data ? (
          <div className="space-y-2">
            <div className="cn text-base font-bold text-slate-900 leading-tight">
              {data.cn}
              {data.pos && (
                <span className="ml-2 text-xs font-semibold text-slate-400">
                  · {data.pos}
                </span>
              )}
            </div>
            {(data.example_en || data.example_cn) && (
              <div className="pt-2 border-t border-slate-100 text-xs space-y-1">
                {data.example_en && (
                  <div className="text-slate-600 italic">
                    &ldquo;{data.example_en}&rdquo;
                  </div>
                )}
                {data.example_cn && (
                  <div className="cn text-slate-800">{data.example_cn}</div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Splits an English string into hoverable word tokens.
 * Non-word text (spaces, punctuation) is rendered verbatim.
 * Skips very short tokens (< 3 chars) — they aren't dictionary-worthy.
 */
export function HoverText({ text, className }) {
  if (!text) return null;
  const parts = text.split(/(\s+|[.,!?;:"'()[\]{}—–-])/g);
  return (
    <span className={className}>
      {parts.map((part, i) => {
        const isWord = /^[A-Za-z][A-Za-z'-]{2,}$/.test(part);
        return isWord ? (
          <HoverWord key={i} word={part} />
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </span>
  );
}

export default HoverText;
